import { useEffect, useRef, useState } from "react"
import { Modal } from "./Modal"
import { useAuth } from "@/state/AuthContext"
import { Icon } from "./Icon"

export interface LoginDialogProps {
  open: boolean
  onClose: () => void
}

type Method = "extension" | "nsec" | "npub"

/** Sign-in dialog: NIP-07 extension, in-memory nsec, or read-only npub. */
export function LoginDialog({ open, onClose }: LoginDialogProps) {
  const { hasExtension, loginWithExtension, loginWithNsec, loginWithNpub, accounts, switchAccount } = useAuth()
  const [method, setMethod] = useState<Method>(hasExtension ? "extension" : "npub")
  const [nsec, setNsec] = useState("")
  const [npub, setNpub] = useState("")
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)
  const errorRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (!open) {
      setError(undefined)
      setBusy(false)
    }
  }, [open])

  const run = async (action: () => Promise<void> | void) => {
    setBusy(true)
    setError(undefined)
    try {
      await action()
      onClose()
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : String(loginError))
      requestAnimationFrame(() => errorRef.current?.focus())
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Log in" description="Choose how you want to sign events.">
      <div className="login-tabs" role="tablist" aria-label="Login method">
        <button
          type="button"
          role="tab"
          aria-selected={method === "extension"}
          className={`login-tab ${method === "extension" ? "active" : ""}`}
          onClick={() => setMethod("extension")}
        >
          <Icon name="globe" size={16} /> Extension
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={method === "nsec"}
          className={`login-tab ${method === "nsec" ? "active" : ""}`}
          onClick={() => setMethod("nsec")}
        >
          <Icon name="eye-off" size={16} /> Private key
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={method === "npub"}
          className={`login-tab ${method === "npub" ? "active" : ""}`}
          onClick={() => setMethod("npub")}
        >
          <Icon name="eye" size={16} /> Read only
        </button>
      </div>

      {method === "extension" && (
        <div className="login-panel">
          <p className="muted">
            A NIP-07 browser extension (Alby, nos2x, …) signs events without exposing your key to this site.
          </p>
          {!hasExtension && <p className="notice">No extension was detected in this browser.</p>}
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => run(loginWithExtension)}
          >
            Log in with extension
          </button>
        </div>
      )}

      {method === "nsec" && (
        <form
          className="login-panel"
          onSubmit={(event) => {
            event.preventDefault()
            void run(() => loginWithNsec(nsec))
          }}
        >
          <label htmlFor="nsec-input">Private key (nsec1…)</label>
          <input
            id="nsec-input"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={nsec}
            onChange={(event) => setNsec(event.target.value)}
            placeholder="nsec1…"
          />
          <p className="muted small">
            The key stays in memory for this tab only. It is never written to storage — reloading the page
            ends the signing session.
          </p>
          <button type="submit" className="btn btn-primary" disabled={busy || nsec.length < 10}>
            Sign in
          </button>
        </form>
      )}

      {method === "npub" && (
        <form
          className="login-panel"
          onSubmit={(event) => {
            event.preventDefault()
            void run(() => loginWithNpub(npub))
          }}
        >
          <label htmlFor="npub-input">Public key (npub1… or nprofile1…)</label>
          <input
            id="npub-input"
            value={npub}
            onChange={(event) => setNpub(event.target.value)}
            placeholder="npub1…"
            spellCheck={false}
          />
          <p className="muted small">Read-only mode: you can browse everything but cannot publish.</p>
          <button type="submit" className="btn btn-primary" disabled={busy || npub.length < 10}>
            Browse read-only
          </button>
        </form>
      )}

      {error && (
        <p className="form-error" role="alert" tabIndex={-1} ref={errorRef}>
          {error}
        </p>
      )}

      {accounts.length > 0 && (
        <div className="login-accounts">
          <h3>Saved accounts</h3>
          {accounts.map((account) => (
            <button
              key={account.pubkey}
              type="button"
              className="account-row"
              onClick={() => run(() => switchAccount(account.pubkey))}
            >
              <Icon name={account.method === "nip07" ? "globe" : account.method === "nsec" ? "eye-off" : "eye"} size={14} />
              <span>{account.pubkey.slice(0, 16)}…</span>
              <span className="badge">{account.method === "npub" ? "read-only" : account.method}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  )
}
